// src/components/Material3/MD3Dialog.jsx
import React, { memo, useCallback, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import './MD3Dialog.css';
import useModalFocus from '../../hooks/useModalFocus';
import MD3Button from './MD3Button';

const MD3Dialog = memo(({
  open = false,
  onClose,
  title,
  descriptionId,
  children,
  actions,
  variant = 'basic',
  fullscreen = false,
  maxWidth = 'sm',
  scrollable = false,
  dividers = false,
  className = '',
  // Theme bridging for portals
  dataTheme,
  themeClass = '',
  closeOnScrim = true,
  initialFocusRef,
  ...props
}) => {
  const dialogRef = useRef();
  const reactId = useId().replace(/:/g, '');
  const titleId = `md3-dialog-title-${reactId}`;
  const handleDismiss = useCallback((event, reason = 'dismissed') => {
    onClose?.(event, reason);
  }, [onClose]);

  useModalFocus({ open, containerRef: dialogRef, onDismiss: handleDismiss, initialFocusRef });

  if (!open) return null;

  const dialogClasses = [
    'md3-dialog',
    `md3-dialog--${variant}`,
    `md3-dialog--${maxWidth}`,
    fullscreen && 'md3-dialog--fullscreen',
    scrollable && 'md3-dialog--scrollable',
    dividers && 'md3-dialog--dividers',
    'md3-dialog--opening',
    className
  ].filter(Boolean).join(' ');

  const dialogContent = (
    <div 
      className={`md3-dialog__scrim ${themeClass}`.trim()}
      data-theme={dataTheme}
      role="presentation"
    >
      {closeOnScrim && (
        <button
          type="button"
          className="md3-dialog__scrim-dismiss"
          tabIndex={-1}
          aria-hidden="true"
          onClick={(event) => handleDismiss(event, 'backdropClick')}
        />
      )}
      <div
        ref={dialogRef}
        className={dialogClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={descriptionId}
        aria-label={!title ? props['aria-label'] : undefined}
        tabIndex={-1}
        {...props}
      >
        <div className="md3-dialog__container">
          {title && (
            <div className="md3-dialog__header">
              <h2 id={titleId} className="md3-dialog__title">
                {title}
              </h2>
            </div>
          )}
          
          <div className="md3-dialog__content">
            {children}
          </div>
          
          {actions && (
            <div className="md3-dialog__actions">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
});

MD3Dialog.displayName = 'MD3Dialog';

// Dialog Actions Helper Component
export const MD3DialogActions = memo(({ 
  children, 
  alignment = 'end',
  stacked = false,
  className = '' 
}) => {
  const classes = [
    'md3-dialog-actions',
    `md3-dialog-actions--${alignment}`,
    stacked && 'md3-dialog-actions--stacked',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {children}
    </div>
  );
});

MD3DialogActions.displayName = 'MD3DialogActions';

// Alert Dialog Variant
export const MD3AlertDialog = memo(({
  open,
  onClose,
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  destructive = false,
  ...props
}) => {
  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  return (
    <MD3Dialog
      open={open}
      onClose={onClose}
      title={title}
      variant="alert"
      {...props}
    >
      {message && (
        <p className="md3-dialog__message">
          {message}
        </p>
      )}
      
      <MD3DialogActions>
        <MD3Button variant="text" onClick={onClose}>
          {cancelText}
        </MD3Button>
        <MD3Button
          variant="text"
          className={destructive ? 'md3-button--error' : ''}
          onClick={handleConfirm}
        >
          {confirmText}
        </MD3Button>
      </MD3DialogActions>
    </MD3Dialog>
  );
});

MD3AlertDialog.displayName = 'MD3AlertDialog';

export default MD3Dialog;
